import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';
import { AdminTrendChart, type ChartMetric, type TrendPoint } from './AdminTrendChart';

interface GuestStats {
  activeGuestAccounts: number;
  totalGuestAccounts: number;
  newGuestAccounts7d: number;
  newGuestAccounts30d: number;
  guestRunsToday: number;
  activeGuestPlayersToday: number;
  guestConversions7d: number;
  guestConversions30d: number;
  legacyGuestRunsToday: number;
}

interface RunsTodaySummary {
  total: number;
  todayPuzzle: number;
  archives: number;
  guest: number;
  registered: number;
  anonymous: number;
  won: number;
  lost: number;
  inProgress: number;
  onLeaderboard: number;
  todayPuzzleNumber: number | null;
}

interface RunsTodayRow {
  id: string;
  status: string;
  score: number;
  dezoomsUsed: number;
  startedAt: string;
  finishedAt: string | null;
  displayName: string;
  isGuest: boolean;
  puzzleNumber: number;
  publishDate: string;
  pool: string;
  isTodayPuzzle: boolean;
  onLeaderboard: boolean;
}

interface RunsTodayDetail {
  date: string;
  summary: RunsTodaySummary;
  truncated: boolean;
  runs: RunsTodayRow[];
}

interface AdminStats {
  totalRegisteredUsers: number;
  newUsers7d: number;
  newUsers30d: number;
  premiumUsers: number;
  runsToday: number;
  runs7d: number;
  winRatePct: number | null;
  arenaMatches30d: number;
  stripePurchases30d: number;
  d1RetentionPct: number | null;
  signupGrowth: { date: string; new_guests: number; new_registered: number }[];
  puzzlesByStatus: { pool: string; status: string; count: number }[];
  guestStats?: GuestStats;
  runsTodayDetail?: RunsTodayDetail;
}

interface TrendsResponse {
  periodDays: number;
  series: TrendPoint[];
}

type PeriodDays = 7 | 30 | 90;

const PERIODS: PeriodDays[] = [7, 30, 90];

const COLOR_GUEST = '#a78bfa';
const COLOR_REGISTERED = '#38bdf8';
const COLOR_CONVERSION = '#34d399';
const COLOR_GUEST_RUNS = '#f472b6';
const COLOR_GUEST_PLAYERS = '#fb923c';
const COLOR_TOTAL_RUNS = '#94a3b8';

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="admin-stat-card">
      <span className="admin-stat-value">{value}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  );
}

function fmtPct(v: number | null) {
  return v == null ? t('admin.stats.noData') : `${v}%`;
}

function fmtParisTime(iso: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: 'Europe/Paris',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function statusLabel(status: string) {
  if (status === 'won') return t('admin.stats.statusWon');
  if (status === 'lost') return t('admin.stats.statusLost');
  return t('admin.stats.statusInProgress');
}

function periodLabel(days: PeriodDays) {
  if (days === 7) return t('admin.stats.period7d');
  if (days === 90) return t('admin.stats.period90d');
  return t('admin.stats.period30d');
}

export function AdminStatsTab() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [trends, setTrends] = useState<TrendsResponse | null>(null);
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [error, setError] = useState('');
  const [trendsLoading, setTrendsLoading] = useState(false);

  useEffect(() => {
    api<AdminStats>('/admin/stats')
      .then(setStats)
      .catch(() => setError(t('common.error')));
  }, []);

  const loadTrends = useCallback((days: PeriodDays) => {
    setTrendsLoading(true);
    api<TrendsResponse>(`/admin/stats/trends?days=${days}`)
      .then(setTrends)
      .catch(() => setError(t('common.error')))
      .finally(() => setTrendsLoading(false));
  }, []);

  useEffect(() => {
    loadTrends(period);
  }, [period, loadTrends]);

  if (error && !stats) return <p className="admin-msg">{error}</p>;
  if (!stats) return <p className="admin-msg">{t('common.loading')}</p>;

  const guest = stats.guestStats ?? {
    activeGuestAccounts: 0,
    totalGuestAccounts: 0,
    newGuestAccounts7d: 0,
    newGuestAccounts30d: 0,
    guestRunsToday: 0,
    activeGuestPlayersToday: 0,
    guestConversions7d: 0,
    guestConversions30d: 0,
    legacyGuestRunsToday: 0,
  };

  const runsDetail = stats.runsTodayDetail;
  const runsSummary = runsDetail?.summary;
  const todayPuzzleLabel = t('admin.stats.runsTodayPuzzle').replace(
    '{n}',
    runsSummary?.todayPuzzleNumber != null ? String(runsSummary.todayPuzzleNumber) : '—',
  );

  const trendSeries = trends?.series ?? [];

  const signupMetrics: ChartMetric[] = [
    { key: 'newGuestAccounts', label: t('admin.stats.chartGuestSignups'), color: COLOR_GUEST },
    { key: 'newRegisteredUsers', label: t('admin.stats.chartRegisteredSignups'), color: COLOR_REGISTERED },
  ];

  const conversionMetrics: ChartMetric[] = [
    { key: 'guestConversions', label: t('admin.stats.chartConversions'), color: COLOR_CONVERSION },
  ];

  const activityMetrics: ChartMetric[] = [
    { key: 'guestOfficialRuns', label: t('admin.stats.chartGuestRuns'), color: COLOR_GUEST_RUNS },
    { key: 'activeGuestPlayers', label: t('admin.stats.chartGuestPlayers'), color: COLOR_GUEST_PLAYERS },
    { key: 'totalOfficialRuns', label: t('admin.stats.chartTotalRuns'), color: COLOR_TOTAL_RUNS, type: 'line' },
  ];

  return (
    <div className="admin-stats">
      <section className="admin-panel-section">
        <h2>{t('admin.stats.sectionUsers')}</h2>
        <div className="admin-stat-grid">
          <StatCard label={t('admin.stats.registeredUsers')} value={stats.totalRegisteredUsers} />
          <StatCard label={t('admin.stats.newUsers7d')} value={stats.newUsers7d} />
          <StatCard label={t('admin.stats.newUsers30d')} value={stats.newUsers30d} />
          <StatCard label={t('admin.stats.premiumUsers')} value={stats.premiumUsers} />
        </div>
      </section>

      <section className="admin-panel-section">
        <h2>{t('admin.stats.sectionGuests')}</h2>
        <div className="admin-stat-grid">
          <StatCard label={t('admin.stats.guestActive')} value={guest.activeGuestAccounts} />
          <StatCard label={t('admin.stats.guestAccounts')} value={guest.totalGuestAccounts} />
          <StatCard label={t('admin.stats.guestNew7d')} value={guest.newGuestAccounts7d} />
          <StatCard label={t('admin.stats.guestNew30d')} value={guest.newGuestAccounts30d} />
          <StatCard label={t('admin.stats.guestRunsToday')} value={guest.guestRunsToday} />
          <StatCard label={t('admin.stats.guestPlayersToday')} value={guest.activeGuestPlayersToday} />
          <StatCard label={t('admin.stats.guestConversions7d')} value={guest.guestConversions7d} />
          <StatCard label={t('admin.stats.guestConversions30d')} value={guest.guestConversions30d} />
        </div>
        {guest.legacyGuestRunsToday > 0 && (
          <p className="admin-chart-note">
            {t('admin.stats.legacyGuestNote').replace('{count}', String(guest.legacyGuestRunsToday))}
          </p>
        )}
      </section>

      <section className="admin-panel-section">
        <h2>{t('admin.stats.sectionActivity')}</h2>
        <div className="admin-stat-grid">
          <StatCard label={t('admin.stats.runsToday')} value={stats.runsToday} />
          <StatCard label={t('admin.stats.runs7d')} value={stats.runs7d} />
          <StatCard label={t('admin.stats.winRate')} value={fmtPct(stats.winRatePct)} />
          <StatCard label={t('admin.stats.arenaMatches')} value={stats.arenaMatches30d} />
          <StatCard label={t('admin.stats.stripePurchases')} value={stats.stripePurchases30d} />
          <StatCard label={t('admin.stats.d1Retention')} value={fmtPct(stats.d1RetentionPct)} />
        </div>
      </section>

      {runsDetail && runsSummary && (
        <section className="admin-panel-section">
          <h2>{t('admin.stats.runsTodayDetail')}</h2>
          <p className="admin-muted-hint">{t('admin.stats.runsTodayHint')}</p>
          <div className="admin-stat-grid">
            <StatCard label={todayPuzzleLabel} value={runsSummary.todayPuzzle} />
            <StatCard label={t('admin.stats.runsArchives')} value={runsSummary.archives} />
            <StatCard label={t('admin.stats.runsOnLeaderboard')} value={runsSummary.onLeaderboard} />
            <StatCard label={t('admin.stats.runsInProgress')} value={runsSummary.inProgress} />
            <StatCard label={t('admin.stats.runsWon')} value={runsSummary.won} />
            <StatCard label={t('admin.stats.runsLost')} value={runsSummary.lost} />
            <StatCard label={t('admin.stats.runsGuest')} value={runsSummary.guest} />
            <StatCard label={t('admin.stats.runsRegistered')} value={runsSummary.registered} />
          </div>
          {runsDetail.truncated && (
            <p className="admin-chart-note">
              {t('admin.stats.runsTruncated').replace('{total}', String(runsSummary.total))}
            </p>
          )}
          {runsDetail.runs.length === 0 ? (
            <p className="admin-msg">{t('admin.stats.noRunsToday')}</p>
          ) : (
            <div className="admin-table-scroll">
              <table className="admin-table admin-table-compact">
                <thead>
                  <tr>
                    <th>{t('admin.stats.colTime')}</th>
                    <th>{t('admin.stats.colPlayer')}</th>
                    <th>{t('admin.stats.colType')}</th>
                    <th>{t('admin.stats.colPuzzle')}</th>
                    <th>{t('admin.stats.colStatus')}</th>
                    <th>{t('admin.stats.colScore')}</th>
                    <th>{t('admin.stats.colLeaderboard')}</th>
                  </tr>
                </thead>
                <tbody>
                  {runsDetail.runs.map((run) => (
                    <tr key={run.id}>
                      <td>{fmtParisTime(run.startedAt)}</td>
                      <td>{run.displayName}</td>
                      <td>
                        <span className={`admin-badge${run.isGuest ? '' : ' admin-badge-muted'}`}>
                          {run.isGuest ? t('admin.stats.typeGuest') : t('admin.stats.typeRegistered')}
                        </span>
                      </td>
                      <td>
                        #{run.puzzleNumber}{' '}
                        <span className="admin-muted-dash">
                          ({run.isTodayPuzzle
                            ? t('admin.stats.puzzleToday')
                            : t('admin.stats.puzzleArchive')}
                          )
                        </span>
                      </td>
                      <td>
                        <span
                          className={`admin-badge${
                            run.status === 'won'
                              ? ' admin-badge-premium'
                              : run.status === 'lost'
                                ? ' admin-badge-danger'
                                : ' admin-badge-muted'
                          }`}
                        >
                          {statusLabel(run.status)}
                        </span>
                      </td>
                      <td>{run.status === 'in_progress' ? '—' : run.score}</td>
                      <td>{run.onLeaderboard ? t('admin.stats.yes') : t('admin.stats.no')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="admin-panel-section admin-trends">
        <div className="admin-trends-head">
          <h2>{t('admin.stats.trendsTitle')}</h2>
          <div className="admin-period-tabs" role="tablist" aria-label={t('admin.stats.trendsTitle')}>
            {PERIODS.map((days) => (
              <button
                key={days}
                type="button"
                role="tab"
                aria-selected={period === days}
                className={`btn secondary admin-btn-sm${period === days ? ' active' : ''}`}
                onClick={() => setPeriod(days)}
              >
                {periodLabel(days)}
              </button>
            ))}
          </div>
        </div>

        {trendsLoading && !trendSeries.length ? (
          <p className="admin-msg">{t('common.loading')}</p>
        ) : (
          <>
            <div className="admin-chart-block">
              <h3>{t('admin.stats.trendsSignups')}</h3>
              <AdminTrendChart series={trendSeries} metrics={signupMetrics} />
            </div>
            <div className="admin-chart-block">
              <h3>{t('admin.stats.trendsConversions')}</h3>
              <AdminTrendChart series={trendSeries} metrics={conversionMetrics} />
            </div>
            <div className="admin-chart-block">
              <h3>{t('admin.stats.trendsGuestActivity')}</h3>
              <AdminTrendChart series={trendSeries} metrics={activityMetrics} height={200} />
            </div>
          </>
        )}
      </section>

      {stats.puzzlesByStatus.length > 0 && (
        <section className="admin-panel-section">
          <h2>{t('admin.stats.puzzlesBreakdown')}</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Pool</th>
                <th>Status</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {stats.puzzlesByStatus.map((row) => (
                <tr key={`${row.pool}-${row.status}`}>
                  <td>{row.pool}</td>
                  <td>{row.status}</td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
